/**
 * Tres líneas contra un eje de meses. Un solo eje vertical, siempre.
 *
 * Nunca dos escalas verticales: es el error más común de un gráfico financiero
 * y hace que dos series se crucen donde no se cruzan. Dos magnitudes de escala
 * distinta van en dos gráficos, no en dos ejes.
 */

import { useId, useState } from "react";
import { formatMillions, formatMonth } from "../format";
import { Leyenda, type Serie } from "./Leyenda";
import { useAncho } from "./medida";
import { type Cifra, cifras, escala, extremos, marcas, ruta } from "./escala";

export interface SerieTemporal extends Serie {
  /** Crudo de la API: puede venir como cadena. Se convierte al entrar. */
  valores: Cifra[];
}

interface Props {
  meses: string[];
  series: SerieTemporal[];
  alto?: number;
}

const MARGEN = { arriba: 10, derecha: 12, abajo: 22, izquierda: 54 };

export function LineasTiempo({ meses, series, alto = 200 }: Props) {
  const [activo, setActivo] = useState<number | null>(null);
  const id = useId();
  // El `viewBox` toma el ancho REAL de la caja para que la escala sea 1:1.
  const [caja, ancho] = useAncho();

  // La conversión va AQUÍ y una sola vez: a partir de esta línea el gráfico
  // trabaja con números, no con lo que la API haya decidido mandar.
  const limpias = series.map((s) => ({ ...s, valores: cifras(s.valores) }));
  const todos = limpias.flatMap((s) => s.valores).filter((v): v is number => v !== null);
  if (!meses.length || !todos.length) return null;

  const dominio = extremos(todos);
  const y = escala(dominio, [alto - MARGEN.abajo, MARGEN.arriba]);
  const x = (i: number) =>
    MARGEN.izquierda +
    (meses.length === 1 ? 0 : (i / (meses.length - 1)) * (ancho - MARGEN.izquierda - MARGEN.derecha));

  /** Lo que el tooltip enseña al puntero, dicho para quien no lo ve. */
  const rotulo = (i: number) =>
    `${formatMonth(meses[i])}: ` +
    limpias.map((s) => `${s.label} ${formatMillions(s.valores[i])}`).join("; ");

  return (
    <div className="viz" ref={caja}>
      <Leyenda series={limpias} />
      <svg
        className="viz-lienzo"
        viewBox={`0 0 ${ancho} ${alto}`}
        preserveAspectRatio="none"
        role="img"
        aria-labelledby={id}
        onMouseLeave={() => setActivo(null)}
      >
        <title id={id}>
          {limpias.map((s) => s.label).join(", ")} por mes
        </title>
        {marcas(dominio).map((v) => (
          <g key={v}>
            <line
              className="viz-rejilla"
              x1={MARGEN.izquierda}
              x2={ancho - MARGEN.derecha}
              y1={y(v)}
              y2={y(v)}
            />
            <text className="viz-rotulo-eje" x={MARGEN.izquierda - 8} y={y(v) + 3} textAnchor="end">
              {formatMillions(v)}
            </text>
          </g>
        ))}
        {limpias.map((s) => (
          <path
            key={s.id}
            className={`viz-linea serie-${s.slot}`}
            d={ruta(s.valores, x, y)}
            fill="none"
          />
        ))}
        {meses.map((mes, i) => (
          <g key={mes}>
            {/* La zona sensible es MÁS ANCHA que la marca: apuntar a una línea
                de 2px con el ratón es una prueba de puntería, no una interfaz. */}
            {/* Enfocable, igual que en las barras: sin esto los valores de la
                serie solo existen para el ratón. */}
            <rect
              className="viz-zona"
              x={x(i) - (ancho / meses.length) / 2}
              y={0}
              width={ancho / meses.length}
              height={alto}
              tabIndex={0}
              role="img"
              aria-label={rotulo(i)}
              onMouseEnter={() => setActivo(i)}
              onFocus={() => setActivo(i)}
              onBlur={() => setActivo(null)}
            />
            {activo === i && (
              <line
                className="viz-cruz"
                x1={x(i)}
                x2={x(i)}
                y1={MARGEN.arriba}
                y2={alto - MARGEN.abajo}
              />
            )}
            {(i === 0 || i === meses.length - 1 || meses.length <= 8) && (
              <text className="viz-rotulo-eje" x={x(i)} y={alto - 6} textAnchor="middle">
                {formatMonth(mes)}
              </text>
            )}
          </g>
        ))}
      </svg>
      {activo !== null && (
        <div className="viz-tooltip" role="status">
          <strong>{formatMonth(meses[activo])}</strong>
          {limpias.map((s) => (
            <div key={s.id}>
              <span className={`viz-marca serie-${s.slot}`} aria-hidden="true" />
              {s.label}
              <span className="num">{formatMillions(s.valores[activo])}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
